// Mock data for Sago Industries single-page clone
// NOTE: All data here is mocked for the frontend-only build.

import {
  Handshake,
  Rocket,
  BadgeIndianRupee,
  FlaskConical,
  ShoppingCart,
  ClipboardCheck,
  MessagesSquare,
  Search,
  ShieldCheck,
  Ship,
} from "lucide-react";

export const LOGO = {
  full: "https://customer-assets-agu9un31.emergentagent.net/job_sago-preview/artifacts/ddjre6i4_Sago-3.png",
  icon: "https://customer-assets-agu9un31.emergentagent.net/job_sago-preview/artifacts/ewljmvni_Sago.png",
};

export const NAV_LINKS = [
  { label: "Home", href: "#home" },
  { label: "Services", href: "#services" },
  { label: "How It Works", href: "#process" },
  { label: "Why Sago", href: "#why" },
  { label: "Contact", href: "#contact" },
];

export const HERO = {
  eyebrow: "Sourcing Goods In India",
  titleLead: "Your Trusted",
  titleHighlight: "Sourcing Partner",
  description:
    "At Sago Industries, we understand the critical factors that drive successful procurement. Whether you're a small business or a large enterprise, our dedicated team ensures a seamless sourcing experience from India.",
  primaryCta: "Get In Touch With Us",
  secondaryCta: "Explore Services",
  pills: [
    "Holistic Approach",
    "Strategic Negotiations",
    "Sample Procurement",
    "Manufacturing Expertise",
    "Reliable Sourcing Network",
    "Quality Assurance",
    "Shipping Solutions",
  ],
  image:
    "https://images.unsplash.com/photo-1605732562742-3023a888e56e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NDh8MHwxfHNlYXJjaHwzfHxzaGlwcGluZyUyMGNvbnRhaW5lcnN8ZW58MHx8fHwxNzg2ODU1Nzg2fDA&ixlib=rb-4.1.0&q=85",
};

export const STATS = [
  { value: "500+", label: "Verified Suppliers" },
  { value: "8 hrs", label: "Response Time" },
  { value: "40+", label: "Countries Served" },
  { value: "99%", label: "On-Time Delivery" },
];

export const SERVICES = [
  {
    icon: Rocket,
    title: "Quick Launch Selected Manufacturers",
    description:
      "Our robust supply chain spans diverse industries, connecting us with carefully chosen suppliers who offer top-quality products and services. Mitigate trade risks and streamline operations.",
  },
  {
    icon: BadgeIndianRupee,
    title: "Free Sourcing Process in India",
    description:
      "Our procurement process is both cost-free and efficient. We earn a reasonable commission only when you buy. Enjoy complimentary quality inspections and temporary storage services.",
  },
  {
    icon: FlaskConical,
    title: "Turn Product Ideas Into Samples",
    description:
      "We transform your product ideas into market-ready solutions with idea protection, seamless manufacturer communication, and efficient product development.",
  },
  {
    icon: ShoppingCart,
    title: "E-commerce Seller Solutions",
    description:
      "A comprehensive suite for sellers on Amazon, Shopify and eBay: customization, rigorous quality inspections, labeling, secure packaging and door-to-door delivery.",
  },
  {
    icon: ClipboardCheck,
    title: "Efficient Production Follow-up",
    description:
      "As a trusted Indian purchasing agent, we handle order tracking and quality inspections with timely updates and rigorous checks during production.",
  },
  {
    icon: MessagesSquare,
    title: "Super Fast 1-on-1 Service",
    description:
      "When you submit an inquiry, a dedicated representative assists you promptly. Despite time zones, we commit to responding within 8 hours.",
  },
];

export const PROCESS = [
  {
    step: "01",
    icon: Search,
    title: "Share Your Requirement",
    description:
      "Tell us what you want to source. Our team reviews your specifications and sourcing goals within hours.",
  },
  {
    step: "02",
    icon: Handshake,
    title: "Supplier Matching",
    description:
      "We match you with vetted manufacturers from our reliable network and negotiate the best terms on your behalf.",
  },
  {
    step: "03",
    icon: ShieldCheck,
    title: "Sample & Quality Check",
    description:
      "Get physical samples, complimentary inspections and rigorous quality assurance before you commit to bulk.",
  },
  {
    step: "04",
    icon: Ship,
    title: "Ship & Deliver",
    description:
      "We handle packaging, labeling and custom export logistics for seamless door-to-door delivery worldwide.",
  },
];

export const BEFORE = {
  title: "Before Using Sago",
  items: [
    {
      title: "Inefficient Resource Procurement",
      description:
        "Navigating multiple suppliers consumes valuable time due to endless comparisons, with no reliable partner to help.",
    },
    {
      title: "Lack of Reliable Representation",
      description:
        "Without a trustworthy partner, navigating business complexities and cultural nuances becomes challenging.",
    },
    {
      title: "Supplier Overload",
      description:
        "Juggling more than 10 Indian suppliers can be overwhelming and hard to keep efficient.",
    },
    {
      title: "Shipping Uncertainties",
      description:
        "Unfamiliar shipping processes can lead to unexpected charges and costly surprises.",
    },
  ],
};

export const AFTER = {
  title: "After Using Sago",
  items: [
    {
      title: "Efficient Product Sourcing",
      description:
        "Discover quality products and reliable suppliers swiftly, ensuring a competitive edge for your business.",
    },
    {
      title: "Trusted Indian Partner",
      description:
        "A dependable local partner who acts as an extension of your team and streamlines operations.",
    },
    {
      title: "Supplier & Order Management",
      description:
        "Effortlessly handle multiple suppliers and orders, minimizing logistical complexities.",
    },
    {
      title: "Custom Export Solutions",
      description:
        "Flexible transportation tailored to your needs, backed by stable pricing for seamless global trade.",
    },
  ],
};

export const ABOUT = {
  title: "An extension of your team, on the ground in India",
  description:
    "Sago Industries bridges the gap between global buyers and India's finest manufacturers. We combine local expertise with a rigorous, transparent process so you can source with total confidence.",
  bullets: [
    "Carefully vetted supplier network",
    "Complimentary quality inspections",
    "Transparent, commission-based pricing",
    "Dedicated 1-on-1 representative",
  ],
  image:
    "https://images.unsplash.com/photo-1553413077-190dd305871c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzZ8MHwxfHNlYXJjaHwyfHx3YXJlaG91c2V8ZW58MHx8fHwxNzg2ODU1Nzg2fDA&ixlib=rb-4.1.0&q=85",
};

export const CONTACT = {
  phone: "+91-992-048-1302",
  email: "assist@sago-industries.com",
  address:
    "C/501, Kamal Park, LBS Marg, Bhandup, Bandra Suburban, Maharashtra, India - 400078",
  image:
    "https://images.unsplash.com/photo-1521791136064-7986c2920216?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzR8MHwxfHNlYXJjaHwzfHxidXNpbmVzcyUyMGhhbmRzaGFrZXxlbnwwfHx8fDE3ODY4NTU3ODZ8MA&ixlib=rb-4.1.0&q=85",
};
